import React from 'react';
import { Editor } from '@tiptap/react';
import { ChainedCommands } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextAlignCenterIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/align-center-icon';
import { RichTextAlignJustifyIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/align-justify-icon';
import { RichTextAlignLeftIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/align-left-icon';
import { RichTextAlignRightIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/align-right-icon';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';

export type RichTextTextAlign = 'left' | 'center' | 'right' | 'justify';

export interface RichTextTextAlignButtonProps extends RichTextButtonProps {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * The text alignment to apply.
   */
  align: RichTextTextAlign;
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Whether the button should hide when the alignment is not available.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

export const richTextTextAlignIcons = {
  left: RichTextAlignLeftIcon,
  center: RichTextAlignCenterIcon,
  right: RichTextAlignRightIcon,
  justify: RichTextAlignJustifyIcon,
};

export const richTextTextAlignShortcutKeys: Partial<
  Record<RichTextTextAlign, string>
> = {
  left: 'Ctrl-Shift-l',
  center: 'Ctrl-Shift-e',
  right: 'Ctrl-Shift-r',
  justify: 'Ctrl-Shift-j',
};

export const richTextTextAlignLabels: Record<RichTextTextAlign, string> = {
  left: 'Align left',
  center: 'Align center',
  right: 'Align right',
  justify: 'Align justify',
};

export function richTextHasSetTextAlign(
  commands: ChainedCommands,
): commands is ChainedCommands & {
  setTextAlign: (align: RichTextTextAlign) => ChainedCommands;
} {
  return 'setTextAlign' in commands;
}

export function richTextCheckTextAlignExtension(
  editor: Editor | null,
): boolean {
  if (!editor) return false;

  const hasExtension = editor.extensionManager.extensions.some(
    (extension) => extension.name === 'textAlign',
  );

  if (!hasExtension) {
    console.warn(
      'TextAlign extension is not available. ' +
        'Make sure it is included in your editor configuration.',
    );
  }

  return hasExtension;
}

export function richTextCanSetTextAlign(
  editor: Editor | null,
  align: RichTextTextAlign,
  alignAvailable: boolean,
): boolean {
  if (!editor || !alignAvailable) return false;

  try {
    return editor.can().setTextAlign(align);
  } catch {
    return false;
  }
}

export function richTextIsTextAlignActive(
  editor: Editor | null,
  align: RichTextTextAlign,
): boolean {
  if (!editor) return false;
  return editor.isActive({ textAlign: align });
}

export function richTextSetTextAlign(
  editor: Editor | null,
  align: RichTextTextAlign,
): boolean {
  if (!editor) return false;

  const chain = editor.chain().focus();
  if (richTextHasSetTextAlign(chain)) {
    return chain.setTextAlign(align).run();
  }
  return false;
}

export function richTextIsTextAlignButtonDisabled(
  editor: Editor | null,
  alignAvailable: boolean,
  canAlign: boolean,
  userDisabled: boolean = false,
): boolean {
  if (!editor || !alignAvailable) return true;
  if (userDisabled) return true;
  if (!canAlign) return true;
  return false;
}

export function richTextShouldShowTextAlignButton(
  editor: Editor | null,
  canAlign: boolean,
  hideWhenUnavailable: boolean,
): boolean {
  if (!editor?.isEditable) return false;
  if (hideWhenUnavailable && !canAlign) return false;
  return true;
}

export function useRichTextTextAlign(
  editor: Editor | null,
  align: RichTextTextAlign,
  disabled: boolean = false,
  hideWhenUnavailable: boolean = false,
) {
  const alignAvailable = React.useMemo(
    () => richTextCheckTextAlignExtension(editor),
    [editor],
  );

  const canAlign = React.useMemo(
    () => richTextCanSetTextAlign(editor, align, alignAvailable),
    [editor, align, alignAvailable],
  );

  const isDisabled = richTextIsTextAlignButtonDisabled(
    editor,
    alignAvailable,
    canAlign,
    disabled,
  );
  const isActive = richTextIsTextAlignActive(editor, align);

  const handleAlignment = React.useCallback(() => {
    if (!alignAvailable || !editor || isDisabled) return false;
    return richTextSetTextAlign(editor, align);
  }, [alignAvailable, editor, isDisabled, align]);

  const shouldShow = React.useMemo(
    () =>
      richTextShouldShowTextAlignButton(editor, canAlign, hideWhenUnavailable),
    [editor, canAlign, hideWhenUnavailable],
  );

  const Icon = richTextTextAlignIcons[align];
  const shortcutKey = richTextTextAlignShortcutKeys[align];
  const label = richTextTextAlignLabels[align];

  return {
    alignAvailable,
    canAlign,
    isDisabled,
    isActive,
    handleAlignment,
    shouldShow,
    Icon,
    shortcutKey,
    label,
  };
}

export const RichTextTextAlignButton = React.forwardRef<
  HTMLButtonElement,
  RichTextTextAlignButtonProps
>(
  (
    {
      editor: providedEditor,
      align,
      text,
      hideWhenUnavailable = false,
      className = '',
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const editor = useRichTextTiptapEditor(providedEditor);

    const {
      isDisabled,
      isActive,
      handleAlignment,
      shouldShow,
      Icon,
      shortcutKey,
      label,
    } = useRichTextTextAlign(editor, align, disabled, hideWhenUnavailable);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !disabled) {
          handleAlignment();
        }
      },
      [onClick, disabled, handleAlignment],
    );

    if (!shouldShow || !editor || !editor.isEditable) {
      return null;
    }

    return (
      <RichTextButton
        type="button"
        className={className.trim()}
        disabled={isDisabled}
        data-style="ghost"
        data-active-state={isActive ? 'on' : 'off'}
        data-disabled={isDisabled}
        role="button"
        tabIndex={-1}
        aria-label={label}
        aria-pressed={isActive}
        tooltip={label}
        shortcutKeys={shortcutKey}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children || (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </RichTextButton>
    );
  },
);

RichTextTextAlignButton.displayName = 'TextAlignButton';

export default RichTextTextAlignButton;
